import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { adminService, authService } from '../../services/api';
import { AdminUserDetail, AdminActivityDetail, ActivityReviewRequest } from '../../types';
import { 
    Box, Typography, Paper, Chip, CircularProgress, Grid, List, ListItem, ListItemText, Divider, Button,
    Accordion, AccordionSummary, AccordionDetails, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Card, CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

const AdminUserDetailComponent: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [childActivities, setChildActivities] = useState<{ [childId: number]: AdminActivityDetail[] }>({});
  const [loadingActivities, setLoadingActivities] = useState<{ [childId: number]: boolean }>({});
  const [processingActivity, setProcessingActivity] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('authUser');
      navigate('/login');
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const data = await adminService.getAdminUserDetail(Number(userId));
        setUser(data);
      } catch (err) {
        setError('Error al cargar el usuario');
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchUser();
  }, [userId]);

  const fetchChildActivities = async (childId: number, forceReload = false) => {
    if (childActivities[childId] && !forceReload) return; // Ya cargadas
    
    try {
      setLoadingActivities(prev => ({ ...prev, [childId]: true }));
      const activities = await adminService.getAdminChildActivities(childId);
      setChildActivities(prev => ({ ...prev, [childId]: activities }));
    } catch (err) {
      console.error('Error al cargar actividades del niño:', err);
    } finally {
      setLoadingActivities(prev => ({ ...prev, [childId]: false }));
    }
  };

  const handleApprove = async (childId: number, activityId: number) => {
    try {
      setProcessingActivity(activityId);
      const reviewData: ActivityReviewRequest = {
        activity_id: activityId,
        status: 'approved'
      };
      
      await adminService.reviewActivity(activityId, reviewData);
      
      // Forzar recarga completa de las actividades del niño
      await fetchChildActivities(childId, true);
      
    } catch (err) {
      console.error('Error al aprobar actividad:', err);
    } finally {
      setProcessingActivity(null);
    }
  };

  const handleReject = async (childId: number, activityId: number) => {
    try {
      setProcessingActivity(activityId);
      const reviewData: ActivityReviewRequest = {
        activity_id: activityId,
        status: 'rejected'
      };
      
      await adminService.reviewActivity(activityId, reviewData);
      
      // Forzar recarga completa de las actividades del niño
      await fetchChildActivities(childId, true);
      
    } catch (err) {
      console.error('Error al rechazar actividad:', err);
    } finally {
      setProcessingActivity(null);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
  }

  if (error || !user) {
    return <Typography color="error">{error || 'Usuario no encontrado'}</Typography>;
  }

  return (
    <Box p={3}>
      {/* Header con botones de navegación y logout */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/users')}>
          Volver a Usuarios
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: '#ffebee'
            }
          }}
        >
          Cerrar Sesión
        </Button>
      </Box>
      
      <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>Detalle de Usuario</Typography>
      
      {/* Card de usuario */}
      <Card elevation={6} sx={{ mb: 4, background: 'linear-gradient(135deg, #7C3AED 0%, #22C55E 100%)', color: '#fff' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '2rem' }}>
              <PersonIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>{user.full_name}</Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>{user.email}</Typography>
              <Chip 
                label={user.is_active ? 'Activo' : 'Inactivo'} 
                sx={{ 
                  bgcolor: user.is_active ? '#22C55E' : '#6B7280', 
                  color: '#fff', 
                  fontWeight: 700,
                  mt: 1 
                }} 
                size="small" 
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>Niños registrados</Typography>
      {user.children.length === 0 ? (
        <Typography color="text.secondary">Este usuario no tiene niños registrados.</Typography>
      ) : (
        <Box>
          {user.children.map(child => (
            <Accordion key={child.id} sx={{ mb: 2, boxShadow: 4, borderRadius: 2 }}>
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                onClick={() => fetchChildActivities(child.id, false)}
                sx={{ 
                  background: 'linear-gradient(90deg, #f3e8ff 0%, #dcfce7 100%)',
                  '&:hover': { background: 'linear-gradient(90deg, #e9d5ff 0%, #bbf7d0 100%)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight={700} color="primary">{child.full_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nacimiento: {child.birth_date} | País: {child.country}
                    </Typography>
                    <Box display="flex" gap={2} mt={1}>
                      <Chip 
                        label={`Libros: ${child.unlocked_books.length > 0 ? child.unlocked_books.join(', ') : 'Ninguno'}`} 
                        color="secondary" 
                        variant="outlined" 
                        size="small" 
                      />
                      <Chip 
                        label={`Actividades: ${child.activities_count}`} 
                        color="primary" 
                        variant="outlined" 
                        size="small" 
                      />
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {loadingActivities[child.id] ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : childActivities[child.id] && childActivities[child.id].length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ background: 'linear-gradient(90deg, #7C3AED 0%, #22C55E 100%)' }}>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Imagen</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Actividad</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Fecha</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Estado</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Revisor</TableCell>
                          <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {childActivities[child.id].map((activity) => (
                          <TableRow key={activity.id} hover sx={{ transition: 'background 0.2s', '&:hover': { background: '#f3e8ff' } }}>
                            <TableCell>
                              <Avatar 
                                variant="rounded" 
                                src={activity.image_url} 
                                alt={`Actividad ${activity.id}`} 
                                sx={{ width: 60, height: 60, cursor: 'pointer' }}
                                onClick={() => window.open(activity.image_url, '_blank')}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                Libro {activity.book_number} - Actividad {activity.activity_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {new Date(activity.submitted_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={activity.status === 'pending' ? 'Pendiente' : activity.status === 'approved' ? 'Aprobado' : 'Rechazado'} 
                                color={activity.status === 'pending' ? 'warning' : activity.status === 'approved' ? 'success' : 'error'} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {activity.reviewer_name || 'Sin revisar'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {activity.status === 'pending' && (
                                <Box display="flex" gap={1}>
                                  <Button 
                                    variant="contained" 
                                    color="success" 
                                    size="small" 
                                    onClick={() => handleApprove(child.id, activity.id)}
                                    disabled={processingActivity === activity.id}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                  >
                                    {processingActivity === activity.id ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      '✅ Aprobar'
                                    )}
                                  </Button>
                                  <Button 
                                    variant="contained" 
                                    color="error" 
                                    size="small" 
                                    onClick={() => handleReject(child.id, activity.id)}
                                    disabled={processingActivity === activity.id}
                                    sx={{ borderRadius: 2, fontWeight: 700 }}
                                  >
                                    {processingActivity === activity.id ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      '❌ Rechazar'
                                    )}
                                  </Button>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ p: 2 }}>
                    Este niño no ha subido actividades aún.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AdminUserDetailComponent; 